/**
 * Tags, and the many-to-many join that carries them. Deleting either side
 * takes the links with it — the foreign keys cascade, so nothing here has to
 * remember to tidy up.
 */
import { insert, query, remove, run, update } from '../db'
import { freshId } from './categories'
import { state } from './state'
import { count } from './usage'
import { dataChanged } from './live'

const fold = (name) => String(name ?? '').trim().toLowerCase()

export async function loadTags() {
  state.tags = await query('SELECT * FROM tags ORDER BY name COLLATE NOCASE')
  // Which rows wear what is the database's to say (filters, search, counts):
  // a tag coming or going changes their answers.
  dataChanged()
  return state.tags
}

/** The existing tag of that name, whatever its case. */
export const findTag = (name) => state.tags.find((t) => fold(t.name) === fold(name)) ?? null

/** Creates a tag, or hands back the one that already goes by that name. */
export async function addTag(name) {
  const clean = String(name ?? '').trim()
  if (!clean) return null
  const existing = findTag(clean)
  if (existing) return existing
  const now = new Date().toISOString()
  const row = { id: await freshId('tg', 'tags', clean), name: clean, created_at: now, updated_at: now }
  await insert('tags', row)
  count('tag.add')
  await loadTags()
  return row
}

export async function renameTag(id, name) {
  const clean = String(name ?? '').trim()
  if (!clean) return
  const clash = findTag(clean)
  if (clash && clash.id !== id) return
  await update('tags', id, { name: clean, updated_at: new Date().toISOString() })
  await loadTags()
}

export async function deleteTag(id) {
  await remove('tags', id)
  await loadTags()
}

/** Replaces a transaction's tags with exactly this set. */
export async function setTransactionTags(transactionId, tagIds) {
  if (!transactionId) return
  await run('DELETE FROM transaction_tags WHERE transaction_id = ?', [transactionId])
  for (const tagId of new Set(tagIds ?? [])) {
    await run(
      'INSERT OR IGNORE INTO transaction_tags (transaction_id, tag_id) VALUES (?, ?)',
      [transactionId, tagId],
    )
  }
  await loadTags()
}

/** The tag ids put on a transaction itself, as its row was read (`ownTags`). */
export const tagIdsOf = (tx) => tx?.ownTags ?? []
